// src/ui/Mixer.js
// Per-track mixer strip: name, volume slider, pan knob, reverb send,
// mute/solo buttons, and a delete button.

import { t, onLocaleChange } from '../i18n.js';

export class Mixer {
  constructor(rootEl, trackManager) {
    this.root = rootEl;
    this.tm = trackManager;
    this._unsub = this.tm.onChange(() => this.render());
    this.render();

    onLocaleChange(() => this.render());
  }

  render() {
    this.root.innerHTML = '';
    if (this.tm.tracks.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'mixer-empty';
      empty.textContent = t('mixer.empty');
      this.root.appendChild(empty);
      return;
    }

    for (const t of this.tm.tracks) {
      this.root.appendChild(this._buildStrip(t));
    }
  }

  _buildStrip(t) {
    const strip = document.createElement('div');
    strip.className = 'mixer-strip';
    strip.style.setProperty('--strip-color', t.color);
    if (!t.isReady) strip.classList.add('strip-loading');

    const header = document.createElement('div');
    header.className = 'strip-header';
    const status = t.isReady ? '' : '<span class="strip-status">Loading…</span>';
    header.innerHTML = `
      <span class="strip-name">${escape(t.name)}</span>
      ${status}
      <button class="strip-x" title="Remove track">×</button>
    `;
    header.querySelector('.strip-x').addEventListener('click', () => this.tm.removeTrack(t.id));
    strip.appendChild(header);

    // Volume
    strip.appendChild(this._slider({
      label: 'Vol', min: -40, max: 6, step: 0.5, value: t.mixer.volume,
      onInput: (v) => t.setMixer({ volume: v }),
      format: (v) => `${v.toFixed(1)} dB`
    }));

    // Pan
    strip.appendChild(this._slider({
      label: 'Pan', min: -1, max: 1, step: 0.01, value: t.mixer.pan,
      onInput: (v) => t.setMixer({ pan: v }),
      format: (v) => v === 0 ? 'C' : (v < 0 ? `L${Math.round(-v * 100)}` : `R${Math.round(v * 100)}`)
    }));

    // Reverb send
    strip.appendChild(this._slider({
      label: 'Rev', min: 0, max: 1, step: 0.01, value: t.mixer.reverbSend,
      onInput: (v) => t.setMixer({ reverbSend: v }),
      format: (v) => `${Math.round(v * 100)}%`
    }));

    // Mute / Solo
    const buttons = document.createElement('div');
    buttons.className = 'strip-buttons';
    const muteBtn = document.createElement('button');
    muteBtn.textContent = 'M';
    muteBtn.className = 'ms-btn' + (t.mixer.muted ? ' active' : '');
    muteBtn.title = 'Mute';
    muteBtn.addEventListener('click', () => t.setMixer({ muted: !t.mixer.muted }));

    const soloBtn = document.createElement('button');
    soloBtn.textContent = 'S';
    soloBtn.className = 'ms-btn solo' + (t.mixer.soloed ? ' active' : '');
    soloBtn.title = 'Solo';
    soloBtn.addEventListener('click', () => t.setMixer({ soloed: !t.mixer.soloed }));

    buttons.appendChild(muteBtn);
    buttons.appendChild(soloBtn);
    strip.appendChild(buttons);

    return strip;
  }

  _slider({ label, min, max, step, value, onInput, format }) {
    const wrap = document.createElement('label');
    wrap.className = 'strip-slider';
    wrap.innerHTML = `
      <span class="slider-label">${label}</span>
      <input type="range" min="${min}" max="${max}" step="${step}" value="${value}" />
      <span class="slider-value">${format(value)}</span>
    `;
    const input = wrap.querySelector('input');
    const valEl = wrap.querySelector('.slider-value');
    input.addEventListener('input', () => {
      const v = +input.value;
      valEl.textContent = format(v);
      onInput(v);
    });
    return wrap;
  }
}

function escape(s) {
  return String(s).replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c]));
}
